import React, { useState, useMemo, RefObject } from "react";
import Dialog from "./Dialog";
import { DialogHandle } from "./DialogTypes";
import Explanation from "./Explanation";
import { Autocomplete, TextField } from "@mui/material";
import { useServices } from "../../services/Services";
import css from "../../common-styling/Dialog.module.css";

const inputStyle = {
  padding: "7px",
  background: "var(--main-background)",
  color: "var(--main-text)",
  border: "1px solid var(--box-border-color)",
  borderRadius: "4px",
  fontSize: "inherit",
  fontFamily: "inherit",
  width: "100%",
  boxSizing: "border-box" as const,
};

interface CountryOption {
  id: string;
  name: string;
}

const CreateDyadDialog: React.FC<{
  ref: RefObject<DialogHandle | null>;
}> = function ({ ref }) {
  const [countryA, setCountryA] = useState<CountryOption | null>(null);
  const [countryB, setCountryB] = useState<CountryOption | null>(null);
  const [companyProfileContext, setCompanyProfileContext] = useState("");

  const services = useServices();
  const { data: user } = services.User.useGetCurrent();
  const { data: employer } = services.User.useGetEmployer(user ?? undefined);
  const { data: countries } = services.Request.useGetCountries();
  const createDyadMutation = services.Request.useCreateDyad();

  const countryOptions: CountryOption[] = useMemo(() => {
    if (!countries) return [];
    return countries
      .filter((c) => c.name)
      .map((c) => ({ id: c.countryId, name: c.name! }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [countries]);

  const countryAOptions = useMemo(
    () => countryOptions.filter((c) => c.id !== countryB?.id),
    [countryOptions, countryB],
  );
  const countryBOptions = useMemo(
    () => countryOptions.filter((c) => c.id !== countryA?.id),
    [countryOptions, countryA],
  );

  async function handleSubmit() {
    if (!countryA || !countryB) {
      throw new Error("Both countries are required.");
    }
    if (!employer?.name) {
      throw new Error("Unable to determine your company. Please try again.");
    }

    await createDyadMutation.mutateAsync({
      countryA: countryA.id,
      countryB: countryB.id,
      company: employer.name,
      companyProfileContext: companyProfileContext.trim() || undefined,
      userId: user!.id,
    });

    setCountryA(null);
    setCountryB(null);
    setCompanyProfileContext("");

    return false;
  }

  return (
    <Dialog
      className={css.dialog}
      ref={ref}
      title="Create Dyad"
      action="Create Dyad"
      onSubmit={handleSubmit}
      onError={(err) => {
        const message = err instanceof Error ? err.message : String(err);
        if (
          message.includes("duplicate") ||
          message.includes("already exists")
        ) {
          return "A dyad for this country pair already exists.";
        }
        if (message.includes("same country") || message.includes("identical")) {
          return "Country A and Country B must be different.";
        }
        return (
          message || "Something went wrong creating the dyad. Please try again."
        );
      }}
    >
      <div className={css["field-group"]}>
        <div className={css.label}>
          Country A{" "}
          <Explanation>First country in the bilateral pair</Explanation>
        </div>
        <div className={css.field}>
          <Autocomplete
            size="small"
            options={countryAOptions}
            getOptionLabel={(opt) => opt.name}
            value={countryA}
            onChange={(_e, value) => setCountryA(value)}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                placeholder="Select a country…"
                className={css.autocomplete}
              />
            )}
            disablePortal
          />
        </div>
      </div>

      <div className={css["field-group"]}>
        <div className={css.label}>
          Country B{" "}
          <Explanation>Second country in the bilateral pair</Explanation>
        </div>
        <div className={css.field}>
          <Autocomplete
            size="small"
            options={countryBOptions}
            getOptionLabel={(opt) => opt.name}
            value={countryB}
            onChange={(_e, value) => setCountryB(value)}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                placeholder="Select a country…"
                className={css.autocomplete}
              />
            )}
            disablePortal
          />
        </div>
      </div>

      <div className={css["field-group"]}>
        <div className={css.label}>
          Company Profile Context{" "}
          <Explanation>
            How is the company connected to this country pair? The more specific
            you are, the more relevant the assessment will be. 2–4 sentences is
            ideal.
          </Explanation>
        </div>
        <ul className={css.hintList}>
          <li>What you trade between them (commodities, direction, volume)</li>
          <li>Operations, assets, or JVs in either country</li>
          <li>Key customers, suppliers, or counterparties</li>
          <li>Shipping routes or logistics dependencies</li>
          <li>Regulatory or licensing exposure</li>
        </ul>
        <div className={css.field}>
          <textarea
            value={companyProfileContext}
            onChange={(e) =>
              setCompanyProfileContext(e.target.value.slice(0, 1000))
            }
            maxLength={1000}
            placeholder={
              "e.g. BHP exports iron ore from Australia to Indian steel mills (JSW, Tata) under long-term offtake contracts. India accounts for ~8% of seaborne iron ore demand. BHP also sources contract engineering services from Indian IT firms (TCS, Infosys) for its Australian operations."
            }
            rows={4}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default CreateDyadDialog;
